import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from './components/Header';
import Hero from './components/Hero';
import Profile from './components/Profile';
import ProfileDetail from './components/ProfileDetail';
import ActivitiesDetail from './components/ActivitiesDetail';
import ActivityDetail from './components/ActivityDetail';
import Policies from './components/Policies';
import Activities from './components/Activities';
import Contact from './components/Contact';
import ContactDetail from './components/ContactDetail';
import ScrollToTop from './components/ScrollToTop';
import ScrollToHash from './components/ScrollToHash';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import ActivityForm from './components/ActivityForm';
import Footer from './components/Footer';
import ImagePrefetcher from './components/ImagePrefetcher';
import Meta from './components/Meta';
import NotFound from './components/NotFound';
import JsonLd from './components/JsonLd';
import './App.css';

function App() {
  return (
    <Router>
      <ScrollToTop />
      <ScrollToHash />
      <div className="App">
        <ImagePrefetcher />
        <Header />
        <main id="main-content">
        <Routes>
          <Route path="/" element={
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.0 }}
            >
              <Meta
                title="うのつのぶこ | 公式サイト"
                description="うのつのぶこ｜町田市の未来を、対話でつくる。プロフィール・政策・活動報告を掲載。"
                canonical="https://unotsunobuko.com/"
                ogTitle="うのつのぶこ | 公式サイト"
                ogDescription="町田市の未来を、対話でつくる。プロフィール・政策・活動報告を掲載。"
                ogImage="https://unotsunobuko.com/ogp-default.jpg"
              />
              <JsonLd
                data={[
                  {
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": "うのつのぶこ",
                    "url": "https://unotsunobuko.com/",
                    "inLanguage": "ja-JP",
                    "image": {
                      "@type": "ImageObject",
                      "url": "https://unotsunobuko.com/unotsu_katsudo.jpg"
                    },
                    "potentialAction": {
                      "@type": "SearchAction",
                      "target": "https://unotsunobuko.com/?q={search_term_string}",
                      "query-input": "required name=search_term_string"
                    }
                  },
                  {
                    "@context": "https://schema.org",
                    "@type": "Person",
                    "name": "うのつのぶこ",
                    "alternateName": "宇野津信子",
                    "jobTitle": "市議会議員候補",
                    "description": "編集者・ライターとして27年の経験を持ち、町田市の地域メディア「玉川つばめ通信」を運営。市民との対話を通じてより住みやすい町田を目指す。",
                    "url": "https://unotsunobuko.com/",
                    "image": "https://unotsunobuko.com/unotsu_katsudo.jpg",
                    "address": {
                      "@type": "PostalAddress",
                      "addressLocality": "町田市",
                      "addressRegion": "東京都",
                      "addressCountry": "JP"
                    },
                    "knowsAbout": ["地域メディア", "編集・ライティング", "町田市政", "再開発問題", "市民活動"],
                    "sameAs": [
                      "https://line.me/R/ti/p/@296ekiii",
                      "https://www.instagram.com/unotsu1973/",
                      "https://www.facebook.com/profile.php?id=100027572019849"
                    ]
                  },
                  {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                      { "@type": "ListItem", "position": 1, "name": "ホーム", "item": "https://unotsunobuko.com/" }
                    ]
                  }
                ]}
              />
              <Hero />
              <Profile />
              <Policies />
              <Activities />
              <Contact />
            </motion.div>
          } />
          <Route path="/profile-detail" element={
            <>
              <Meta
                title="うのつのぶこ | プロフィール"
                description="編集者・ライターとしての経歴や、地域での活動、想いについて。"
                canonical="https://unotsunobuko.com/profile-detail"
                ogTitle="うのつのぶこ | プロフィール"
                ogDescription="編集者・ライターとしての経歴や、地域での活動、想いについて。"
                ogImage="https://unotsunobuko.com/ogp-default.jpg"
              />
              <JsonLd
                data={[
                  {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                      { "@type": "ListItem", "position": 1, "name": "ホーム", "item": "https://unotsunobuko.com/" },
                      { "@type": "ListItem", "position": 2, "name": "プロフィール", "item": "https://unotsunobuko.com/profile-detail" }
                    ]
                  },
                  {
                    "@context": "https://schema.org",
                    "@type": "ProfilePage",
                    "name": "うのつのぶこ | プロフィール",
                    "url": "https://unotsunobuko.com/profile-detail",
                    "inLanguage": "ja-JP",
                    "mainEntity": {
                      "@type": "Person",
                      "name": "うのつのぶこ",
                      "alternateName": "宇野津信子",
                      "url": "https://unotsunobuko.com/"
                    }
                  }
                ]}
              />
              <ProfileDetail />
            </>
          } />
          <Route path="/activities-detail" element={
            <>
              <Meta
                title="うのつのぶこ | 活動報告"
                description="日々の活動の記録やイベント参加の様子を紹介。"
                canonical="https://unotsunobuko.com/activities-detail"
                ogTitle="うのつのぶこ | 活動報告"
                ogDescription="日々の活動の記録やイベント参加の様子を紹介。"
                ogImage="https://unotsunobuko.com/ogp-default.jpg"
              />
              <JsonLd
                data={[
                  {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                      { "@type": "ListItem", "position": 1, "name": "ホーム", "item": "https://unotsunobuko.com/" },
                      { "@type": "ListItem", "position": 2, "name": "活動報告", "item": "https://unotsunobuko.com/activities-detail" }
                    ]
                  },
                  {
                    "@context": "https://schema.org",
                    "@type": "CollectionPage",
                    "name": "うのつのぶこ | 活動報告",
                    "url": "https://unotsunobuko.com/activities-detail",
                    "inLanguage": "ja-JP",
                    "isPartOf": { "@type": "WebSite", "url": "https://unotsunobuko.com/" }
                  }
                ]}
              />
              <ActivitiesDetail />
            </>
          } />
          <Route path="/activities-detail/:id" element={<ActivityDetail />} />
          <Route path="/contact-detail" element={
            <>
              <Meta
                title="うのつのぶこ | お問い合わせ"
                description="LINE・電話・メールでご連絡いただけます。"
                canonical="https://unotsunobuko.com/contact-detail"
                ogTitle="うのつのぶこ | お問い合わせ"
                ogDescription="LINE・電話・メールでご連絡いただけます。"
                ogImage="https://unotsunobuko.com/ogp-default.jpg"
              />
              <JsonLd
                data={[
                  {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                      { "@type": "ListItem", "position": 1, "name": "ホーム", "item": "https://unotsunobuko.com/" },
                      { "@type": "ListItem", "position": 2, "name": "お問い合わせ", "item": "https://unotsunobuko.com/contact-detail" }
                    ]
                  },
                  {
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    "name": "うのつのぶこ | お問い合わせ",
                    "url": "https://unotsunobuko.com/contact-detail",
                    "inLanguage": "ja-JP"
                  }
                ]}
              />
              <ContactDetail />
            </>
          } />
          <Route path="*" element={<NotFound />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/activity/new" element={<ActivityForm />} />
          <Route path="/admin/activity/edit/:id" element={<ActivityForm />} />
        </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
